import { useState, useEffect } from "react"
import { useGetReturnsQuery, useGetReturnStatsQuery, useUpdateReturnStatusMutation } from "../features/admin/adminApi"
import { Package, TrendingDown, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react"

function formatCurrency(amount: number, currency = "NGN") {
  const symbol = currency === "NGN" ? "₦" : currency
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function ReturnsPage() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [selectedReturn, setSelectedReturn] = useState<any>(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [newStatus, setNewStatus] = useState<"pending" | "approved" | "rejected" | "completed">("pending")
  const [adminNotes, setAdminNotes] = useState("")

  const { data: returnStats } = useGetReturnStatsQuery()
  const { data, isLoading, error } = useGetReturnsQuery({
    page,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  })
  const [updateStatus, { isLoading: isUpdating }] = useUpdateReturnStatusMutation()

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const stats = [
    {
      label: "Total Returns",
      value: returnStats?.totalReturns || 0,
      icon: Package,
      color: "blue",
    },
    {
      label: "Pending",
      value: returnStats?.pendingReturns || 0,
      icon: Clock,
      color: "yellow",
    },
    {
      label: "Approved",
      value: returnStats?.approvedReturns || 0,
      icon: CheckCircle,
      color: "green",
    },
    {
      label: "Total Refunds",
      value: formatCurrency(returnStats?.totalRefundAmount || 0),
      icon: TrendingDown,
      color: "purple",
    },
  ]

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
    }
    return badges[status as keyof typeof badges] || "bg-gray-100 text-gray-800"
  }

  const handleUpdateStatus = async () => {
    if (!selectedReturn) return

    try {
      await updateStatus({
        uid: selectedReturn.id,
        status: newStatus,
        adminNotes: adminNotes || undefined,
      }).unwrap()
      setShowStatusModal(false)
      setSelectedReturn(null)
      setAdminNotes("")
      alert("Return status updated successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to update status")
    }
  }

  const openStatusModal = (returnItem: any) => {
    setSelectedReturn(returnItem)
    setNewStatus(returnItem.status)
    setShowStatusModal(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Returns Management</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <stat.icon className={`text-${stat.color}-600`} size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search by order number, user..."
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="completed">Completed</option>
          </select>
          <input
            type="date"
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              setPage(1)
            }}
            placeholder="From Date"
          />
          <input
            type="date"
            className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              setPage(1)
            }}
            placeholder="To Date"
          />
        </div>
      </div>

      {/* Returns List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading returns...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Failed to load returns</div>
        ) : !data?.returns.length ? (
          <div className="p-8 text-center text-gray-500">No returns found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Refund Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.returns.map((returnItem) => (
                    <tr key={returnItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {returnItem.orderNumber || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            Total: {formatCurrency(returnItem.orderTotal)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {returnItem.productImage && (
                            <img
                              src={returnItem.productImage}
                              alt={returnItem.productName || "Product"}
                              className="w-12 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {returnItem.productName || "Unknown Product"}
                            </div>
                            {returnItem.variantName && (
                              <div className="text-xs text-gray-500">{returnItem.variantName}</div>
                            )}
                            <div className="text-xs text-gray-500">Qty: {returnItem.quantity}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {returnItem.userName || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">{returnItem.userEmail}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(returnItem.itemPrice * returnItem.quantity)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate" title={returnItem.reason || "No reason provided"}>
                          {returnItem.reason || "No reason provided"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(returnItem.status)}`}>
                          {returnItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {formatDate(returnItem.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openStatusModal(returnItem)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Update Status
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Page {data.page} of {data.totalPages} ({data.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                    disabled={page === data.totalPages}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Update Status Modal */}
      {showStatusModal && selectedReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Update Return Status</h2>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Order:</span> {selectedReturn.orderNumber}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Product:</span> {selectedReturn.productName}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Customer:</span> {selectedReturn.userName} ({selectedReturn.userEmail})
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <span className="font-medium">Refund Amount:</span> {formatCurrency(selectedReturn.itemPrice * selectedReturn.quantity)}
                </p>
                {selectedReturn.reason && (
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Reason:</span> {selectedReturn.reason}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this decision..."
                />
              </div>

              {newStatus === "approved" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <CheckCircle className="text-green-600" size={20} />
                    <div className="text-sm text-green-800">
                      <p className="font-medium">Approving this return</p>
                      <p>The refund will need to be processed separately.</p>
                    </div>
                  </div>
                </div>
              )}

              {newStatus === "rejected" && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <XCircle className="text-red-600" size={20} />
                    <div className="text-sm text-red-800">
                      <p className="font-medium">Rejecting this return</p>
                      <p>The customer will be notified of the rejection.</p>
                    </div>
                  </div>
                </div>
              )}

              {newStatus === "completed" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <AlertCircle className="text-blue-600" size={20} />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Completing this return</p>
                      <p>Marking the entire return process as complete.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowStatusModal(false)
                  setSelectedReturn(null)
                  setAdminNotes("")
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateStatus}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
